/**
 * The self-serve sign-up screen — F26's journey, reduced to what a trial actually needs.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * WHEN THIS RENDERS, AND WHY THAT IS THE RIGHT TRIGGER
 *
 * A visitor who is **signed in and belongs to no organisation**
 * (`resolveDestination` → `onboard`). That state used to fall through to the ordinary dashboard
 * scoped to no org, where RLS correctly returned zero rows everywhere and there was nothing to
 * do and no explanation — which is why the auth client had refused to let magic links create
 * accounts at all.
 *
 * ⚠️ **The company IS asked before the magic link now — but it is never held in the browser**, and
 * that distinction is the whole point. The sign-up form collects it, the Worker attaches it to the
 * magic-link request as Supabase user metadata, and it arrives back on the session wherever the link
 * is opened. Browser-held pending state fails in the *common* case (the link opened on a phone or a
 * work machine, where PKCE correctly refuses and any stashed value is gone); riding on the user
 * record has no such failure.
 *
 * So this screen still asks — the field is pre-filled, not fixed — because someone may have typed a
 * trading name and want their legal one, and because a value that arrived from elsewhere should be
 * confirmed by the person it belongs to rather than assumed.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT IT ASKS, AND WHAT IT DELIBERATELY DOES NOT
 *
 * Name and region, and nothing else. No plan choice: the trial is one tier for everybody
 * (`free_audit` — "not yet paying"), and asking someone to pick a plan before they have seen the
 * product inverts the reason a trial exists. No card, no Stripe, no billing address — a trial
 * creates no Stripe object at all, which is this slice's whole point.
 *
 * ⚠️ **Region is the one irreversible choice on this screen**, so it says so rather than being
 * quietly recorded: `org.region` is immutable by database trigger the moment the row is written.
 * That is the residency guarantee, not a limitation to apologise for — but a visitor picking it
 * in three seconds deserves to know it is permanent.
 */
import { useState } from 'react';
import { Button, Callout, FieldGroup, HelpText, Input, Radio } from '@dedupr/ui';
import type { GatewayAuth } from '@dedupr/session';
import { InstallOffer } from '../InstallOffer.js';
import { PasswordFields, passwordReady } from '../SetPasswordOffer.js';
import './StartTrial.css';

export interface StartTrialProps {
  /** The signed-in visitor's address, shown so they can tell which account they are setting up. */
  email: string | null;
  /**
   * The company they gave on the sign-up form, carried here on the user record.
   *
   * Pre-filled rather than fixed: it is still an editable field, because someone may have typed
   * their trading name and want their legal one — but they should not have to type it twice, and
   * two independently-typed values are two values that can disagree.
   */
  defaultOrgName?: string;
  /** Resolves when the org exists and routing has been re-resolved; rejects with a message. */
  onStart: (details: { orgName: string; region: 'eu' | 'us' }) => Promise<void>;
  onSignOut: () => void;
  /** Days of trial, for the copy only — the database owns the actual length. */
  trialDays?: number;
  /**
   * The gateway, threaded to `SetPasswordOffer` (ADR-089).
   *
   * ⚠️ **Required, and passed rather than imported.** `SetPasswordOffer` originally read the auth
   * module directly, which made merely *rendering this screen* evaluate `window.location.host` at
   * import time and broke this screen's own install test. Required so a caller cannot quietly drop
   * the password offer by forgetting a prop.
   */
  auth: GatewayAuth | null;
}

const REGIONS: { value: 'eu' | 'us'; label: string; detail: string }[] = [
  {
    value: 'eu',
    label: 'European Union',
    detail: 'Your records are processed and stored in the EU.',
  },
  {
    value: 'us',
    label: 'United States',
    detail: 'Your records are processed and stored in the US.',
  },
];

export function StartTrial({ email, defaultOrgName = '', onStart, onSignOut, trialDays = 7, auth }: StartTrialProps) {
  const [orgName, setOrgName] = useState(defaultOrgName);
  const [region, setRegion] = useState<'eu' | 'us'>('eu');
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = orgName.trim();
  /* Matches the database's 1–200 exactly rather than being stricter: a client-side rule tighter
     than the server's rejects input the product actually accepts. */
  const nameValid = trimmed.length >= 1 && trimmed.length <= 200;
  /**
   * ⚠️ **The password is REQUIRED here** (owner decision, 2026-08-16), which supersedes ADR-089's
   * "optional, offered afterwards" for this screen alone. The reason it was optional was that a
   * link is the default way in — but on an installed iOS app a link **cannot be redeemed at all**
   * (the verifier is in the app's container, the code lands in Safari), and this screen's own next
   * step invites the visitor to install. Optional meant a client could install the app and be
   * locked out of it, having been offered the fix and skipped past it.
   *
   * ⚠️ `#/password` stays optional for **existing** clients, deliberately: making it required there
   * would lock out someone who is already working, to fix a problem they may not have.
   */
  const passwordValid = auth ? passwordReady(password, again) : true;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!nameValid || !passwordValid || busy) return;
    setBusy(true);
    setError(null);
    try {
      /**
       * ⚠️ **The password FIRST, and the order is the safety property.** Both calls can fail, and
       * only one of them is recoverable from this screen: `onStart` re-resolves routing and moves
       * the visitor away, so a password set after it would be set on a screen that is disappearing —
       * and a failure there would leave a client with an organisation, a trial running, and no
       * password, on the one platform where a link cannot get them back in. Setting it first means
       * a failure leaves nothing created and the person simply tries again.
       *
       * ⚠️ Skipped in mock mode, where there is no gateway to set one against — the same reason
       * `SetPasswordOffer` renders nothing without `auth`.
       */
      if (auth) await auth.setPassword(password);
      await onStart({ orgName: trimmed, region });
      /* No success state here on purpose: `onStart` re-resolves routing and the app moves this
         visitor to where they now belong. A "done!" screen would be a second place that decides
         what happens next, and it would be the one that is wrong when provisioning has already
         run. */
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The trial could not be started.');
      setBusy(false);
    }
  }

  return (
    <div className="start-trial">
      <main className="start-trial__card">
        <h1 className="start-trial__title">Set up your organisation</h1>
        <p className="start-trial__lede">
          You are signed in{email ? <> as <strong>{email}</strong></> : null}. One more step and
          your {trialDays}-day trial starts — <strong>no card, no payment details</strong>.
        </p>

        <form onSubmit={submit} noValidate>
          <FieldGroup
            label="Organisation name"
            helpText="Your company or team name. You can change this later."
            required
            error={orgName.length > 0 && !nameValid ? 'Use between 1 and 200 characters.' : undefined}
          >
            <Input
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              autoComplete="organization"
              autoFocus
              maxLength={200}
              disabled={busy}
            />
          </FieldGroup>

          <fieldset className="start-trial__regions" disabled={busy}>
            {/* A real `<fieldset>`/`<legend>`, so a screen reader announces the group's question
                with each option rather than reading two unlabelled radios. */}
            <legend className="start-trial__legend">Where should your data live?</legend>
            <HelpText>
              This is permanent. Your region fixes where your records are processed and stored, and
              it cannot be changed afterwards — moving a client's data between regions would break
              the residency guarantee this choice exists to make.
            </HelpText>
            {REGIONS.map((r) => (
              <div className="start-trial__region" key={r.value}>
                <Radio
                  name="region"
                  value={r.value}
                  checked={region === r.value}
                  onChange={() => setRegion(r.value)}
                  label={r.label}
                />
                <span className="start-trial__region-detail">{r.detail}</span>
              </div>
            ))}
          </fieldset>

          {/* ⚠️ **Inside the form and ABOVE the button** — the layout defect this replaces had the
              primary action floating above an optional-looking card with a second "Save password"
              button below it, so the screen offered two submits and no order. One step, one
              control, and the control is last: a button that sits above fields reads as the end of
              the form, and anything under it reads as an afterthought — which is exactly how a
              required field gets skipped. */}
          {auth ? (
            <fieldset className="start-trial__password">
              <legend>Set a password</legend>
              <p className="start-trial__password-why">
                {/* Says why it is required rather than only that it is. A required field with no
                    stated reason reads as a form being difficult. */}
                You will be offered the installed app next, and on an iPhone an installed app cannot
                use emailed sign-in links at all — it keeps its own storage separate from Safari. A
                password is what lets you sign in inside the app itself.
              </p>
              <PasswordFields
                password={password}
                again={again}
                onPassword={setPassword}
                onAgain={setAgain}
                disabled={busy}
              />
            </fieldset>
          ) : null}

          {error ? (
            /* `assertive`, unlike the ordinary status regions elsewhere: this fires in response to
               the visitor's own submit and it is the only thing standing between them and a
               working account. */
            <div role="alert" aria-live="assertive" className="start-trial__error">
              <Callout tone="danger" title="That did not work">
                {error}
              </Callout>
            </div>
          ) : null}

          <Button type="submit" variant="primary" disabled={!nameValid || !passwordValid || busy}>
            {busy ? 'Starting your trial…' : `Start my ${trialDays}-day trial`}
          </Button>
        </form>

        <p className="start-trial__what-happens">
          <strong>What you get:</strong> the local audit sandbox — install Dedupr on this machine,
          load a CRM export, and see your duplicates found with the evidence behind each one.
          Everything stays in this browser; nothing is written to any system.
        </p>

        {/* ⚠️ Was a promise with no way to keep it: the paragraph above has said "install Dedupr on
            this machine" since this screen was built, and nothing on the page — or anywhere else
            reachable before a real connection existed — ever offered an actual install control.
            Same origin as the rest of this app, so `beforeinstallprompt` can fire here too. */}
        {/* ⚠️ **The title said "before you even sign in" and the reader IS signed in** — this screen
            only exists for a session that has just come through the emailed link. It was written when
            the sign-in screen offered the install first; that offer has moved here (2026-08-14),
            because installing before there is a session opens an app with no way into it. So this is
            now the FIRST install offer anybody sees, and saying so is what makes it land: the window
            it installs inherits the session that is reading this. */}
        <InstallOffer title="Install it as an app — you are signed in, so it opens ready to use">
          It runs the same way either way, but installed it opens from your home screen rather than a
          browser tab you have to find again, and it keeps working without a connection.
        </InstallOffer>

        {/* ⚠️ The standalone `SetPasswordOffer` card WAS here, and its removal is the point: the
            password moved into the form above, where it is required and shares the one submit. Two
            cards each with a button was the defect — the visitor could press "Start my trial" with
            the password box untouched, and on an installed iOS app that is a locked door. The card
            still exists for `#/password`, where it remains genuinely optional for people who
            already have a working account. */}

        <p className="start-trial__footnote">
          {/* Not a marketing aside. A visitor who reaches this screen by mistake — signed in with
              the wrong address, or belonging to an org they were expecting to be invited to —
              needs a way out that is not "create a second organisation". */}
          Expecting to join an organisation that already exists? Ask its owner to invite you, then{' '}
          <button type="button" className="start-trial__link" onClick={onSignOut}>
            sign out
          </button>{' '}
          and use the link in that invitation.
        </p>
      </main>
    </div>
  );
}
